import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { adminApi } from "../../utils/axiosConfig";
import * as XLSX from "xlsx";
import { FaSearch, FaEye, FaEdit, FaTrash, FaCheck, FaTimes, FaEnvelope } from "react-icons/fa";
import { motion } from "framer-motion";
import ModifyCommande from "./ModifyCommande";
import DeleteCommande from "./DeleteCommande";

// STYLES
const Container = styled(motion.div)`
  max-width: 96%;
  margin: 2rem auto;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(22, 163, 74, 0.12);
`;

const Title = styled.h2`
  color: #14532d;
  font-size: 2rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1.5rem;
  border-bottom: 4px solid #16a34a;
  display: inline-block;
  padding-bottom: 0.5rem;
`;

const SearchBar = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  margin-bottom: 1.5rem;
  border: 1px solid #86efac;
  border-radius: 12px;
  font-size: 1rem;
  background: rgba(22, 163, 74, 0.06);
  transition: all 0.2s ease;

  &:focus {
    border-color: #16a34a;
    outline: none;
    box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.18);
    background: rgba(22, 163, 74, 0.1);
  }
`;

const OrderCard = styled.div`
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 1.6rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 6px 24px rgba(22, 163, 74, 0.10);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(22, 163, 74, 0.16);
  }
`;

const UserInfo = styled.p`
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.75rem;
  color: #14532d;

  em {
    color: #166534;
    font-style: italic;
    margin-left: 6px;
  }
`;

const ItemsList = styled.div`
  margin-top: 1.2rem;
`;

const Item = styled.div`
  border-bottom: 1px solid #e2e8f0;
  padding: 0.75rem 0;
  font-size: 0.95rem;
  color: #166534;
  line-height: 1.6;

  &:last-child {
    border-bottom: none;
  }

  p {
    margin: 2px 0;
  }

  strong {
    color: #14532d;
  }
`;

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 0.85rem;
  padding: 6px 14px;
  border-radius: 9999px;
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
  box-shadow: 0 4px 14px rgba(22, 163, 74, 0.18);
  background-color: ${({ status }) => {
    switch ((status || '').toLowerCase()) {
      case "livrée":
        return "#16a34a";
      case "en cours":
        return "#f59e0b";
      case "en attente":
        return "#84cc16";
      case "expédiée":
        return "#10b981";
      case "annulée":
        return "#ef4444";
      default:
        return "#166534";
    }
  }};
  margin-bottom: 0.75rem;
`;

const Total = styled.p`
  font-weight: 800;
  margin-top: 0.75rem;
  color: #166534;
  font-size: 1.05rem;
  background: #ecfdf5;
  border: 1px solid #86efac;
  padding: 0.6rem 1rem;
  border-radius: 12px;
  display: inline-block;
  box-shadow: 0 4px 14px rgba(22, 163, 74, 0.12);
`;

const PromoInfo = styled.p`
  font-weight: 700;
  color: #166534;
  background: #ecfdf5;
  border: 1px solid #86efac;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  margin-top: 0.6rem;
  display: inline-block;
  font-size: 0.9rem;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.1);
`;

// ICONS
const icons = {
  livrée: (
    <svg
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  "en cours": (
    <svg
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  "en attente": (
    <svg
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4l3 3" />
    </svg>
  ),
  expédiée: (
    <svg
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <rect x="2" y="7" width="20" height="10" rx="2" ry="2" />
      <path d="M2 7l10 5 10-5" />
    </svg>
  ),
  annulée: (
    <svg
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  default: (
    <svg
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" />
    </svg>
  ),
};

// COMPONENT
export default function Commandes() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [statusFilter, setStatusFilter] = useState("");
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.get(`/api/admin/orders`, { params: { page, limit, search: search || undefined, status: statusFilter || undefined } });
      const payload = res.data;
      const items = Array.isArray(payload?.items) ? payload.items : (Array.isArray(payload) ? payload : []);
      const total = typeof payload?.total === "number" ? payload.total : items.length;
      setOrders({ items, total });
    } catch (err) {
      console.error("Erreur récupération de toutes les commandes :", err);
      setError("Impossible de charger les commandes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [apiUrl, page, limit, search, statusFilter]);

  const statuses = ["en attente", "en cours", "expédiée", "livrée", "annulée"];
  const statusLabel = {
    "en attente": "En attente",
    "en cours": "En cours",
    "expédiée": "Expédiée",
    "livrée": "Livrée",
    "annulée": "Annulée",
  };
  const statusStyle = (active) => ({
    padding: "6px 12px",
    borderRadius: 18,
    border: `1px solid ${active ? "#15803d" : "#86efac"}`,
    background: active ? "#16a34a" : "#ffffff",
    color: active ? "#ffffff" : "#166534",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    boxShadow: active ? "0 2px 6px rgba(22,163,74,0.25)" : "none",
    transition: "all .2s ease",
  });

  const updateStatus = async (orderId, status) => {
    try {
      await adminApi.put(`/api/admin/orders/update`, { orderId, status });
      fetchOrders();
    } catch (e) {}
  };

  const generateInvoice = (o) => {
    const dateStr = new Date(o.createdAt).toLocaleString();
    const rows = (o.items || []).map((it) => {
      const promoPct = Number(o.promoData?.pourcentage) || 0;
      const base = Number((it.product?.price?.originalPrice ?? it.price) || 0);
      const unit = promoPct > 0 ? base * (1 - promoPct / 100) : Number(it.price || 0);
      const qty = Number(it.quantity || 1);
      const total = (unit * qty).toFixed(2);
      return {
        name: it.product?.name || "",
        variante: `${it.couleur || ""} / ${it.taille || ""}`,
        qty,
        unit: unit.toFixed(2),
        total,
      };
    });
    const grandTotal = Number(o.total || rows.reduce((s, r) => s + Number(r.total), 0)).toFixed(2);
    const html = `<!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Facture - ${o._id}</title>
        <style>
          body { font-family: 'Georgia', serif; color:#1f2937; padding:24px; }
          .header { display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid #e5e7eb; padding-bottom:12px; margin-bottom:16px; }
          .brand { font-weight:700; font-size:20px; color:#14532d; }
          .muted { color:#6b7280; font-size:12px; }
          .section { margin: 16px 0; }
          .card { border:1px solid #e5e7eb; border-radius:10px; padding:14px; background:#fff; }
          .row { display:flex; gap:16px; flex-wrap:wrap; }
          .col { flex:1 1 260px; }
          table { width:100%; border-collapse:collapse; }
          th, td { text-align:left; padding:10px; border-bottom:1px solid #f1f5f9; }
          th { background:#f8fafc; }
          .total { font-weight:700; }
          .footer { margin-top:24px; padding-top:12px; border-top:1px solid #e5e7eb; text-align:center; color:#64748b; font-size:12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="brand">SAFA Store – Facture</div>
            <div class="muted">Essayez d’abord, Payez après</div>
          </div>
          <div class="muted">
            N° facture: ${o._id}<br/>
            Date: ${dateStr}
          </div>
        </div>
        <div class="section row">
          <div class="col card">
            <strong>Vendu par</strong>
            <div>SAFA Store</div>
            <div>support@safa-store.ma</div>
          </div>
          <div class="col card">
            <strong>Client</strong>
            <div>${o.user?.name || ''}</div>
            <div>${o.user?.email || ''}</div>
            <div>${o.user?.phone || ''}</div>
            <div>${o.user?.city || ''}</div>
            <div>${o.user?.address || ''}</div>
            <div>${o.user?.country || ''}</div>
          </div>
        </div>
        <div class="section card">
          <table>
            <thead>
              <tr>
                <th>Produit</th>
                <th>Variante</th>
                <th>Qté</th>
                <th>PU (MAD)</th>
                <th>Total (MAD)</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map(r => `<tr><td>${r.name}</td><td>${r.variante}</td><td>${r.qty}</td><td>${r.unit}</td><td>${r.total}</td></tr>`).join("")}
            </tbody>
          </table>
        </div>
        <div class="section card" style="display:flex; justify-content:flex-end">
          <div style="min-width:240px;">
            <div style="display:flex; justify-content:space-between; padding:6px 0;"><span>Sous-total</span><span>${grandTotal} MAD</span></div>
            <div style="display:flex; justify-content:space-between; padding:6px 0;"><span>Livraison</span><span>Gratuit</span></div>
            <div style="height:1px; background:#e5e7eb; margin:8px 0;"></div>
            <div class="total" style="display:flex; justify-content:space-between; padding:6px 0;"><span>Total</span><span>${grandTotal} MAD</span></div>
          </div>
        </div>
        <div class="footer">© ${new Date().getFullYear()} SAFA Store — Tous droits réservés. Merci pour votre confiance.</div>
        <script>window.onload = () => { window.print(); }</script>
      </body>
    </html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
  };

  const shareOrderWhatsApp = (o) => {
    const dateStr = new Date(o.createdAt).toLocaleString();
    const promoPct = Number(o?.promoData?.pourcentage) || 0;
    const lines = (o.items || []).map((it) => {
      const base = Number((it.product?.price?.originalPrice ?? it.price) || 0);
      const unit = promoPct > 0 ? base * (1 - promoPct / 100) : Number(it.price || 0);
      const qty = Number(it.quantity || 1);
      const line = unit * qty;
      return `- ${it.product?.name || ''} | ${it.couleur || ''}/${it.taille || ''} | ${qty} x ${unit.toFixed(2)} MAD = ${line.toFixed(2)} MAD`;
    }).join("\n");
    const total = Number(o.total || 0).toFixed(2);
    const client = `${o.user?.name || ''} | ${o.user?.email || ''} | ${o.user?.phone || ''}\n${o.user?.address || ''} - ${o.user?.city || ''} - ${o.user?.country || ''}`;
    const statut = o.status || '';
    const commentaire = o.comment || '';
    const header = `SAFA Store — Commande #${String(o._id).slice(-8)}\nDate: ${dateStr}`;
    const body = `Client:\n${client}\n\nArticles:\n${lines}\n\nTotal: ${total} MAD\nStatut: ${statut}\nCommentaire: ${commentaire}`;
    const text = `${header}\n\n${body}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const openEmailModal = (o) => {
    setEmailTarget(o);
    setEmailSubject(`Votre commande #${String(o._id).slice(-8)}`);
    setEmailMessage(`Bonjour ${o.user?.name || ''},\n\n\nCordialement,\nSAFA Store`);
    setEmailModalOpen(true);
  };

  const sendEmail = async () => {
    if (!emailTarget) return;
    try {
      setEmailSending(true);
      await adminApi.post(`/api/admin/orders/${emailTarget._id}/email`, {
        subject: emailSubject,
        message: emailMessage,
      });
      setEmailSending(false);
      setEmailModalOpen(false);
    } catch (e) {
      setEmailSending(false);
      alert("Erreur lors de l'envoi de l'email.");
    }
  };

  const items = Array.isArray(orders?.items) ? orders.items : (Array.isArray(orders) ? orders : []);
  const total = typeof orders?.total === "number" ? orders.total : items.length;

  if (loading)
    return (
      <p style={{ textAlign: "center", marginTop: "2rem" }}>
        Chargement des commandes...
      </p>
    );
  if (error)
    return (
      <p style={{ color: "#e53e3e", textAlign: "center", marginTop: "2rem" }}>
        {error}
      </p>
    );
  if (items.length === 0)
    return (
      <p style={{ textAlign: "center", marginTop: "2rem" }}>
        Aucune commande trouvée.
      </p>
    );

  return (
    <Container
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Title>Toutes les commandes par profil connecté</Title>
      <SearchBar
        type="text"
        placeholder="Rechercher par nom ou email du client..."
        value={search}
        onChange={(e) => { setPage(1); setSearch(e.target.value); }}
      />

      <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "12px 0" }}>
        <select value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }} style={{ padding: 8, border: "1px solid #86efac", borderRadius: 8 }}>
          <option value="">Tous les statuts</option>
          {statuses.map((s) => (
            <option key={s} value={s}>{statusLabel[s]}</option>
          ))}
        </select>
        <select value={limit} onChange={(e) => { setPage(1); setLimit(parseInt(e.target.value, 10)); }} style={{ padding: 8, border: "1px solid #86efac", borderRadius: 8 }}>
          {[5,10,20,50,100].map(n => <option key={n} value={n}>{n}/page</option>)}
        </select>
        <button
          type="button"
          onClick={async () => {
            const res = await adminApi.get(`/api/admin/orders`, { params: { all: true, search: search || undefined } });
            const list = (res.data?.items || res.data || []);
            const rows = [];
            list.forEach((o) => {
              const promoPct = Number(o?.promoData?.pourcentage) || 0;
              (o.items || []).forEach((it) => {
                const base = Number((it.product?.price?.originalPrice ?? it.price) || 0);
                const unit = promoPct > 0 ? base * (1 - promoPct / 100) : Number(it.price || 0);
                rows.push({
                  Date: new Date(o.createdAt).toLocaleString(),
                  Produit: it.product?.name || "",
                  Couleur: it.couleur || "",
                  Taille: it.taille || "",
                  Quantite: it.quantity || 1,
                  PU_MAD: Number(unit).toFixed(2),
                  Ligne_Total_MAD: Number(unit * (it.quantity || 1)).toFixed(2),
                  Client: o.user?.name || "",
                  Email: o.user?.email || "",
                  Telephone: o.user?.phone || "",
                  Ville: o.user?.city || "",
                  Adresse: o.user?.address || "",
                  Pays: o.user?.country || "",
                  Statut: o.status || "",
                  Commentaire: o.comment || "",
                  Commande_Total_MAD: Number(o.total || 0).toFixed(2),
                });
              });
            });
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(rows);
            XLSX.utils.book_append_sheet(wb, ws, 'Orders');
            XLSX.writeFile(wb, 'orders.xlsx');
          }}
          style={{ padding: "8px 12px", borderRadius: 12, border: "1px solid #16a34a", background: "linear-gradient(180deg,#22c55e,#16a34a)", color: "#fff", fontWeight: 700, boxShadow: "0 8px 24px rgba(22,163,74,0.25)", cursor: "pointer" }}
        >
          Export Excel
        </button>
        <button
          type="button"
          onClick={async () => {
            const res = await adminApi.get(`/api/admin/orders`, { params: { all: true, search: search || undefined } });
            const list = (res.data?.items || res.data || []);
            const rows = [];
            list.forEach((o) => {
              const promoPct = Number(o?.promoData?.pourcentage) || 0;
              (o.items || []).forEach((it) => {
                const base = Number((it.product?.price?.originalPrice ?? it.price) || 0);
                const unit = promoPct > 0 ? base * (1 - promoPct / 100) : Number(it.price || 0);
                const line = unit * (it.quantity || 1);
                rows.push({ o, it, unit, line });
              });
            });
            const rowsHtml = rows.map(({o,it,unit,line}) => (
              `<tr>`+
              `<td>${new Date(o.createdAt).toLocaleString()}</td>`+
              `<td>${it.product?.name || ''}</td>`+
              `<td>${(it.couleur||'')} / ${(it.taille||'')}</td>`+
              `<td>${it.quantity || 1}</td>`+
              `<td>${Number(unit).toFixed(2)}</td>`+
              `<td>${Number(line).toFixed(2)}</td>`+
              `<td>${o.user?.name || ''}</td>`+
              `<td>${o.user?.email || ''}</td>`+
              `<td>${o.user?.phone || ''}</td>`+
              `<td>${o.user?.city || ''}</td>`+
              `<td>${o.user?.address || ''}</td>`+
              `<td>${o.user?.country || ''}</td>`+
              `<td>${o.status || ''}</td>`+
              `<td>${o.comment || ''}</td>`+
              `</tr>`
            )).join("");

            const html = `<!doctype html><html><head><meta charset="utf-8"><title>Orders</title></head><body>${
              `<h3>Toutes les commandes</h3>` +
              `<table border="1" cellpadding="6" cellspacing="0">` +
              `<thead><tr><th>Date</th><th>Produit</th><th>Variante</th><th>Qté</th><th>PU (MAD)</th><th>Ligne Total (MAD)</th><th>Client</th><th>Email</th><th>Téléphone</th><th>Ville</th><th>Adresse</th><th>Pays</th><th>Statut</th><th>Commentaire</th></tr></thead>` +
              `<tbody>` + rowsHtml + `</tbody></table>`
            }`;
            const newWindow = window.open("", "_blank");
            newWindow.document.write(html);
            newWindow.document.close();
            newWindow.focus();
            newWindow.print();
          }}
          style={{ padding: "8px 12px", borderRadius: 12, border: "1px solid #16a34a", background: "linear-gradient(180deg,#22c55e,#16a34a)", color: "#fff", fontWeight: 700, boxShadow: "0 8px 24px rgba(22,163,74,0.25)", cursor: "pointer" }}
        >
          Export PDF
        </button>
      </div>
      {emailModalOpen && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.35)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          paddingTop: 40,
          zIndex: 1000,
        }}>
          <div style={{
            width: "min(92vw, 560px)",
            background: "rgba(255,255,255,0.72)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid #86efac",
            borderRadius: 16,
            boxShadow: "0 20px 60px rgba(22,163,74,0.25)",
            padding: 16,
            color: "#14532d",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#14532d" }}>Envoyer un email</h3>
              <button type="button" onClick={() => setEmailModalOpen(false)} style={{
                border: "1px solid #86efac",
                background: "rgba(22,163,74,0.08)",
                color: "#166534",
                borderRadius: 10,
                padding: "6px 10px",
                cursor: "pointer",
                fontWeight: 700,
              }}>Fermer</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 12, color: "#166534", fontWeight: 700 }}>Objet</label>
              <input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} style={{
                padding: 10,
                border: "1px solid #86efac",
                borderRadius: 10,
                outline: "none",
                background: "rgba(22,163,74,0.06)",
              }} />
              <label style={{ fontSize: 12, color: "#166534", fontWeight: 700, marginTop: 4 }}>Message</label>
              <textarea rows={6} value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} style={{
                padding: 10,
                border: "1px solid #86efac",
                borderRadius: 10,
                outline: "none",
                background: "rgba(22,163,74,0.06)",
                resize: "vertical",
                minHeight: 140,
              }} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button type="button" onClick={() => setEmailModalOpen(false)} style={{
                padding: "8px 12px",
                borderRadius: 12,
                border: "1px solid #86efac",
                background: "rgba(22,163,74,0.08)",
                color: "#166534",
                fontWeight: 700,
                cursor: "pointer",
              }}>Annuler</button>
              <button type="button" onClick={sendEmail} disabled={emailSending || !emailSubject || !emailMessage} style={{
                padding: "8px 12px",
                borderRadius: 12,
                border: "1px solid #16a34a",
                background: "linear-gradient(180deg,#22c55e,#16a34a)",
                color: "#fff",
                fontWeight: 800,
                cursor: emailSending ? "not-allowed" : "pointer",
                opacity: emailSending ? 0.7 : 1,
              }}>{emailSending ? "Envoi..." : "Envoyer"}</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 6, whiteSpace: "nowrap" }}>Date</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 6, whiteSpace: "nowrap" }}>Produit</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 6, whiteSpace: "nowrap" }}>Variante</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 6, whiteSpace: "nowrap", width: 50 }}>Qté</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 6, whiteSpace: "nowrap", width: 110 }}>Total</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 6, whiteSpace: "nowrap" }}>Client</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 6, whiteSpace: "nowrap" }}>Téléphone</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 6, whiteSpace: "nowrap" }}>Ville</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 6, whiteSpace: "nowrap" }}>Adresse</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 6, whiteSpace: "nowrap" }}>Statut</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 6, whiteSpace: "nowrap" }}>Commentaire</th>
              <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 6, whiteSpace: "nowrap" }}>Appeler/Facture/Partager</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => {
              const orderItems = Array.isArray(o.items) ? o.items : [];
              return orderItems.map((it, idx) => {
                const promoPct = Number(o?.promoData?.pourcentage) || 0;
                const base = Number((it.product?.price?.originalPrice ?? it.price) || 0);
                const unit = promoPct > 0 ? base * (1 - promoPct / 100) : Number(it.price || 0);
                const line = unit * (it.quantity || 1);
        return (
                <tr key={`${o._id}-${idx}`}>
                  <td style={{ padding: 6 }}>{new Date(o.createdAt).toLocaleString()}</td>
                  <td style={{ padding: 6 }}>{it.product?.name || ""}</td>
                  <td style={{ padding: 6 }}>{(it.couleur||'')} / {(it.taille||'')}</td>
                  <td style={{ padding: 4, width: 50, textAlign: "center" }}>{it.quantity || 1}</td>
                  <td style={{ padding: 4, width: 110, textAlign: "right" }}>{Number(line).toFixed(2)} MAD</td>
                  <td style={{ padding: 6 }}>{o.user?.name || ""} <br/><em style={{ color: "#166534" }}>{o.user?.email || ""}</em></td>
                  <td style={{ padding: 6 }}>{o.user?.phone || ""}</td>
                  <td style={{ padding: 6 }}>{o.user?.city || ""}</td>
                  <td style={{ padding: 6, maxWidth: 200, whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word" }} title={o.user?.address || ""}>
                    {o.user?.address || ""}
                  </td>
                  {idx === 0 && (
                    <td style={{ padding: 6 }} rowSpan={orderItems.length}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {statuses.map((s) => (
                          <button
                            key={s}
                            type="button"
                            style={statusStyle(o.status === s)}
                            onClick={() => updateStatus(o._id, s)}
                          >
                            {statusLabel[s]}
                          </button>
                        ))}
                      </div>
                    </td>
                  )}
                  {idx === 0 && (
                    <td style={{ padding: 6 }} rowSpan={orderItems.length}>
                      <textarea
                        defaultValue={o.comment || ""}
                        onBlur={async (e) => {
                          const val = e.target.value;
                          try { await adminApi.put(`/api/admin/orders/${o._id}/comment`, { comment: val }); } catch {}
                        }}
                        placeholder="Ajouter un commentaire..."
                        rows={2}
                        style={{ width: 100, padding: 8, border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12 }}
                      />
                    </td>
                  )}
                  {idx === 0 && (
                    <td style={{ padding: 6 }} rowSpan={orderItems.length}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <a href={`tel:${o.user?.phone || ''}`} title={`Appeler ${o.user?.phone || ''}`} style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 40,
                          height: 40,
                          minWidth: 40,
                          minHeight: 40,
                          flex: "0 0 40px",
                          borderRadius: "50%",
                          background: "#16a34a",
                          color: "#fff",
                          textDecoration: "none",
                          boxShadow: "0 2px 6px rgba(22,163,74,0.25)",
                        }}>📞</a>
                        <button type="button" onClick={() => generateInvoice(o)} title="Générer facture PDF" style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "8px 12px",
                          borderRadius: 12,
                          border: "1px solid #16a34a",
                          background: "linear-gradient(180deg,#22c55e,#16a34a)",
                          color: "#fff",
                          fontWeight: 700,
                          boxShadow: "0 8px 24px rgba(22,163,74,0.25)",
                          cursor: "pointer",
                        }}>
                          Facture PDF
                        </button>
                        <button type="button" onClick={() => shareOrderWhatsApp(o)} title="Partager sur WhatsApp" style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "8px 12px",
                          borderRadius: 12,
                          border: "1px solid #16a34a",
                          background: "rgba(22,163,74,0.08)",
                          color: "#166534",
                          fontWeight: 700,
                          boxShadow: "0 6px 18px rgba(22,163,74,0.12)",
                          cursor: "pointer",
                        }}>
                          Partager
                        </button>
                        <button type="button" onClick={() => openEmailModal(o)} title="Envoyer un email personnalisé" style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "8px 12px",
                          borderRadius: 12,
                          border: "1px solid #16a34a",
                          background: "rgba(22,163,74,0.08)",
                          color: "#166534",
                          fontWeight: 700,
                          boxShadow: "0 6px 18px rgba(22,163,74,0.12)",
                          cursor: "pointer",
                        }}>
                          <FaEnvelope style={{ marginRight: 6 }} /> Email
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            });
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
        <div style={{ color: "#6b7280", fontSize: 12 }}>
          {items.length} sur {total} éléments — page {page}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button disabled={page<=1} onClick={() => setPage((p)=>Math.max(1,p-1))} style={{ padding: "8px 12px", borderRadius: 12, border: "1px solid #86efac", background: "rgba(22,163,74,0.08)", color: "#166534", fontWeight: 700 }}>Précédent</button>
          <button disabled={(page*limit)>=total} onClick={() => setPage((p)=>p+1)} style={{ padding: "8px 12px", borderRadius: 12, border: "1px solid #86efac", background: "rgba(22,163,74,0.08)", color: "#166534", fontWeight: 700 }}>Suivant</button>
        </div>
      </div>
    </Container>
  );
}
